import React, { useState } from 'react';
import { 
  FileText, 
  Trash2, 
  Archive, 
  Eye,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';

export default function ImportCard({ dataset, onArchive, onDelete, onView }) {
  const [loading, setLoading] = useState(false);

  const getSourceIcon = (sourceType) => {
    switch (sourceType) {
      case 'csv':
        return '📄';
      case 'excel':
        return '📊';
      case 'json':
        return '📋';
      case 'sql':
        return '🗄️';
      default:
        return '📁';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-400 bg-green-500/20';
      case 'processing':
        return 'text-yellow-400 bg-yellow-500/20';
      case 'failed':
        return 'text-red-400 bg-red-500/20';
      default:
        return 'text-slate-400 bg-slate-500/20';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleArchive = async () => {
    if (!window.confirm('Archive this dataset? It will be hidden but not deleted.')) {
      return;
    }
    setLoading(true);
    await onArchive(dataset.id);
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this dataset permanently? This cannot be undone.')) {
      return;
    }
    setLoading(true);
    await onDelete(dataset.id);
    setLoading(false);
  };

  return (
    <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6 hover:border-slate-600 transition-all">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3 flex-1">
          <div className="text-3xl">{getSourceIcon(dataset.source_type)}</div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-white truncate">{dataset.name}</h3>
            <p className="text-sm text-slate-400 truncate">
              {dataset.source_type.toUpperCase()} Import
              {dataset.original_filename && ` • ${dataset.original_filename}`}
            </p>
            {dataset.description && (
              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{dataset.description}</p>
            )}
          </div>
        </div>

        {/* Status Badge */}
        <div className={`flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(dataset.status)}`}>
          {dataset.status === 'completed' && <CheckCircle className="w-3 h-3" />}
          {dataset.status === 'processing' && <Loader2 className="w-3 h-3 animate-spin" />}
          {dataset.status === 'failed' && <AlertCircle className="w-3 h-3" />}
          <span className="capitalize">{dataset.status}</span>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-slate-700/30 rounded-lg p-3">
          <div className="flex items-center space-x-2 text-slate-400 mb-1">
            <FileText className="w-3 h-3" />
            <span className="text-xs">Total Records</span>
          </div>
          <div className="text-lg font-semibold text-white">
            {dataset.record_count || 0}
          </div>
        </div>

        <div className="bg-slate-700/30 rounded-lg p-3">
          <div className="flex items-center space-x-2 text-slate-400 mb-1">
            <CheckCircle className="w-3 h-3" />
            <span className="text-xs">Valid</span>
          </div>
          <div className="text-lg font-semibold text-green-400">
            {dataset.valid_records || 0}
          </div>
        </div>

        <div className="bg-slate-700/30 rounded-lg p-3">
          <div className="flex items-center space-x-2 text-slate-400 mb-1">
            <AlertCircle className="w-3 h-3" />
            <span className="text-xs">Invalid</span>
          </div>
          <div className="text-lg font-semibold text-red-400">
            {dataset.invalid_records || 0}
          </div>
        </div>
      </div>

      {/* Import Info */}
      <div className="flex items-center space-x-4 text-xs text-slate-400 mb-4">
        <div className="flex items-center space-x-1">
          <Clock className="w-3 h-3" />
          <span>Imported {formatDate(dataset.imported_at)}</span>
        </div>
        {dataset.completed_at && (
          <div className="flex items-center space-x-1">
            <CheckCircle className="w-3 h-3" />
            <span>Completed {formatDate(dataset.completed_at)}</span>
          </div>
        )}
      </div>

      {/* Table Name Badge */}
      <div className="mb-4">
        <span className="inline-flex items-center space-x-1 px-2 py-1 rounded text-xs bg-purple-500/20 text-purple-400 font-mono">
          {dataset.table_name}
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center space-x-2">
        <button
          onClick={() => onView(dataset)}
          className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Eye className="w-4 h-4" />
          <span>View Data</span>
        </button>

        <button
          onClick={handleArchive}
          disabled={loading}
          className="flex items-center justify-center space-x-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-colors disabled:opacity-50"
          title="Archive"
        >
          <Archive className="w-4 h-4" />
        </button>

        <button
          onClick={handleDelete}
          disabled={loading}
          className="flex items-center justify-center space-x-2 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg text-sm transition-colors disabled:opacity-50"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
